import React, { useState } from "react";
import { useHistory } from 'react-router-dom';
import { 
    Box, Grid, Typography, TextField, Button, 
    Container, CssBaseline, Avatar 
} from "@mui/material";
import { LoadingButton } from '@mui/lab';
import ReactInputVerificationCode from 'react-input-verification-code';

// Iconos
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faLock, faEnvelopeOpenText, faKey } from "@fortawesome/free-solid-svg-icons";

// Componentes y Controladores
import { changePassword, requestCodeToResetPassword, verifyRequestedCode } from "../../controllers/AuthController";
import ModalDialog from '../../components/modalDialog';
import ReCaptchaGoogle from "../../components/ReCaptchaGoogle";
import logo from '../../assets/images/logos/black.png';

export default function ForgotPassword() {
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(0);

    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');

    const [password, setPassword] = useState('');
    const [errorPassword, setErrorPassword] = useState(false);
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [errorPasswordConfirm, setErrorPasswordConfirm] = useState(false);

    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [msg, setMsg] = useState('');
    const [action, setAction] = useState('Aceptar');

    const [reCaptcha, setReCaptcha] = useState('');

    const history = useHistory();
    const redirect = (route) => history.push(route);

    // --- MANEJADORES DE LÓGICA ---
    const requestCode = async () => {
        if (email === '') return; // Validación básica
        setIsLoading(true);
        let response = await requestCodeToResetPassword(email);
        
        if (response.success === true) {
            setStep(1);
        } else {
            setTitle('Operación Fallida');
            setMsg(response.message);
            setOpen(true);
        }
        setIsLoading(false);
    };

    const verifyCode = async () => {
        if (code.length < 4) return;
        setIsLoading(true);
        let response = await verifyRequestedCode(email, code);
        if (response.success === true) {
            setStep(2);
        } else {
            setTitle('Operación Fallida');
            setMsg(response.message);
            setOpen(true);
        }
        setIsLoading(false);
    };

    const formVerification = () => {
        let ban = 0;
        if (password === '') { setErrorPassword(true); ban = 1; }
        if (passwordConfirm === '') { setErrorPasswordConfirm(true); ban = 1; }
        if (password !== passwordConfirm) { setErrorPasswordConfirm(true); ban = 1; }
        return ban;
    };

    const resetPassword = async () => {
        if (formVerification() === 0) {
            setIsLoading(true);
            let response = await changePassword(email, password, code);
            setTitle(response.success ? '¡Operación Exitosa!' : 'Operación Fallida');
            setMsg(response.message);
            setOpen(true);
            setIsLoading(false);
        }
    };

    const handleAction = () => {
        setOpen(false);
        if (title === '¡Operación Exitosa!') {
            redirect('/auth'); // Vuelve al login genérico
        }
    };

    const handleOnChangeRecaptcha = (value) => setReCaptcha(value);
    const handleOnExpiredRecaptcha = () => setReCaptcha('');

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center',
            bgcolor: '#ffffff',
            p: 3
        }}>
            <CssBaseline />
            
            <Container maxWidth="xs" disableGutters>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                    
                    {/* --- LOGO SUTIL ARRIBA --- 
                    <Box sx={{ mb: 4, opacity: 0.8 }}>
                        <img src={logo} alt="QUD Logo" style={{ width: 60, height: 'auto' }} />
                    </Box>*/}

                    <Box component="form" noValidate sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        
                        {/* --- PASO 0: SOLICITAR CÓDIGO --- */}
                        {step === 0 && (
                            <>
                                <Avatar sx={{ width: 64, height: 64, bgcolor: '#e3f2fd', color: '#1976d2', mb: 3 }}>
                                    <FontAwesomeIcon icon={faLock} size="lg" />
                                </Avatar>
                                
                                <Typography variant="h4" fontWeight="800" color="#111" textAlign="center" gutterBottom>
                                    Recuperar Contraseña
                                </Typography>
                                <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
                                    Ingresa tu correo electrónico y te enviaremos un código para restablecer tu acceso.
                                </Typography>

                                <Box sx={{ width: '100%', mb: 3 }}>
                                    <Typography variant="subtitle2" fontWeight="700" color="#333" mb={0.8}>
                                        Correo Electrónico
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        id="email"
                                        placeholder="ejemplo@correo.com"
                                        name='email'
                                        variant="outlined"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                                    <ReCaptchaGoogle onChange={handleOnChangeRecaptcha} onAbort={handleOnExpiredRecaptcha} onExpired={handleOnExpiredRecaptcha} />
                                </Box>

                                <LoadingButton
                                    disabled={reCaptcha === '' || email === ''}
                                    loading={isLoading}
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    onClick={requestCode}
                                    disableElevation
                                    sx={{ py: 1.8, fontSize: '1rem', fontWeight: '800', borderRadius: 3, textTransform: 'none' }}
                                >
                                    Enviar Código
                                </LoadingButton>
                            </>
                        )}

                        {/* --- PASO 1: VERIFICAR CÓDIGO --- */}
                        {step === 1 && (
                            <>
                                <Avatar sx={{ width: 64, height: 64, bgcolor: '#fff8e1', color: '#ffb300', mb: 3 }}>
                                    <FontAwesomeIcon icon={faEnvelopeOpenText} size="lg" />
                                </Avatar>
                                
                                <Typography variant="h4" fontWeight="800" color="#111" textAlign="center" gutterBottom>
                                    Revisa tu correo
                                </Typography>
                                <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
                                    Hemos enviado un código de 4 dígitos a <br/><strong>{email}</strong>
                                </Typography>
                                
                                <Box sx={{ 
                                    mb: 5, 
                                    '& .ReactInputVerificationCode-item': { 
                                        width: '3.5rem', height: '4rem', fontSize: '1.5rem', fontWeight: 'bold', 
                                        borderRadius: '12px', border: '2px solid #e0e0e0', color: '#222' 
                                    },
                                    '& .ReactInputVerificationCode-item.is-active': { borderColor: 'primary.main', boxShadow: 'none' }
                                }}>
                                    <ReactInputVerificationCode length={4} onChange={(e) => setCode(e)} value={code} />
                                </Box>

                                <LoadingButton
                                    loading={isLoading}
                                    disabled={code.length < 4}
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    onClick={verifyCode}
                                    disableElevation
                                    sx={{ py: 1.8, fontSize: '1rem', fontWeight: '800', borderRadius: 3, textTransform: 'none' }}
                                >
                                    Verificar Código
                                </LoadingButton>
                            </>
                        )}

                        {/* --- PASO 2: NUEVA CONTRASEÑA --- */}
                        {step === 2 && (
                            <>
                                <Avatar sx={{ width: 64, height: 64, bgcolor: '#e8f5e9', color: '#2e7d32', mb: 3 }}>
                                    <FontAwesomeIcon icon={faKey} size="lg" />
                                </Avatar>

                                <Typography variant="h4" fontWeight="800" color="#111" textAlign="center" gutterBottom>
                                    Nueva Contraseña
                                </Typography>
                                <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
                                    Crea una nueva contraseña segura para tu cuenta.
                                </Typography>

                                <Box sx={{ width: '100%', mb: 3 }}>
                                    <Typography variant="subtitle2" fontWeight="700" color="#333" mb={0.8}>
                                        Nueva Contraseña
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="••••••••"
                                        type='password'
                                        value={password}
                                        onChange={(e) => { setPassword(e.target.value); setErrorPassword(false); }}
                                        onBlur={() => setErrorPassword(password === '')}
                                        error={errorPassword}
                                        helperText={errorPassword && 'Requerido'}
                                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    />

                                    <Typography variant="subtitle2" fontWeight="700" color="#333" mb={0.8}>
                                        Confirmar Contraseña
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="••••••••"
                                        type='password'
                                        value={passwordConfirm}
                                        onChange={(e) => { setPasswordConfirm(e.target.value); setErrorPasswordConfirm(false); }}
                                        onBlur={() => setErrorPasswordConfirm(passwordConfirm === '' || passwordConfirm !== password)}
                                        error={errorPasswordConfirm}
                                        helperText={passwordConfirm !== password && passwordConfirm !== '' ? 'No coinciden' : (errorPasswordConfirm && 'Requerido')}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    />
                                </Box>

                                <LoadingButton
                                    loading={isLoading}
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    onClick={resetPassword}
                                    disableElevation
                                    sx={{ py: 1.8, fontSize: '1rem', fontWeight: '800', borderRadius: 3, textTransform: 'none' }}
                                >
                                    Restablecer Contraseña
                                </LoadingButton>
                            </>
                        )}
                        
                        {/* --- BOTÓN VOLVER AL LOGIN --- */}
                        <Box sx={{ mt: 4 }}>
                            <Button 
                                startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                                onClick={() => redirect('/auth')}
                                sx={{ textTransform: 'none', fontWeight: 'bold', color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Volver al inicio de sesión
                            </Button>
                        </Box>

                    </Box>
                </Box>
            </Container>

            <ModalDialog
                open={open}
                handleClose={handleAction}
                title={title}
                msg={msg}
                handleAction={handleAction}
                action={action}
            />
        </Box>
    );
}